import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { ResponsiveContainer, Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Tag, BadgePercent } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, ChartTooltip, PageHeader, QueryState, CALUM, ErrorBox } from '../components/charts';
import DateRangeControl from '../components/DateRangeControl';
import { useApi } from '../hooks/useApi';
import { useDataRange } from '../hooks/useDataRange';
import api from '../lib/api';
import { formatCurrency, formatCurrency2, formatNumber, formatMonth } from '../lib/format';
import { todayISO } from '../lib/day';

const n2 = (v) => (v == null ? '–' : String(v).replace('.', ','));
const Pct = ({ v }) => v == null ? <span className="text-slate-400">—</span> : (
  <span className={v >= 0 ? 'text-emerald-600' : 'text-red-500'}>{v >= 0 ? '+' : ''}{n2(v)}%</span>
);

// Price-change table (risers / fallers) — same columns for both.
const PriceTable = ({ rows, t }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm min-w-[360px]">
      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
        <th className="py-1">{t("Product")}</th><th className="text-right">{t("was")}</th><th className="text-right">{t("nu")}</th><th className="text-right">Δ</th><th className="text-right">{t("stuks")}</th>
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} className="border-t border-slate-100">
            <td className="py-1.5 text-slate-700"><span className="block truncate max-w-[150px]" title={r.name}>{r.name}</span></td>
            <td className="text-right tabular-nums">{formatCurrency2(r.list_first)}</td>
            <td className="text-right tabular-nums">{formatCurrency2(r.list_last)}</td>
            <td className="text-right tabular-nums"><Pct v={r.chg_pct} /></td>
            <td className="text-right tabular-nums text-slate-400">{formatNumber(r.units)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Composition() {
  const t = useT();
  const [range, setRange] = useState({ preset: 'today', start_date: todayISO(), end_date: todayISO() });   // open on the current day
  const p = { start_date: range.start_date, end_date: range.end_date };

  const dataRange = useDataRange();
  const pr = useApi(() => api.priceTrends(p), [range.start_date, range.end_date]);
  const ds = useApi(() => api.discounts(p), [range.start_date, range.end_date]);

  const emp = ds.data?.employee;
  const empMonthly = (ds.data?.employee_monthly || []).map((m) => ({ ...m, label: formatMonth(m.month) }));

  return (
    <PageShell page="Prijzen & korting" accent="gold">
      <PageHeader title={t("Prijzen & korting")} subtitle={t("Menuprijs-wijzigingen op de kaart en de personeelskorting.")}>
        <DateRangeControl value={range} onChange={setRange} dataRange={dataRange} />
      </PageHeader>

      {pr.error && <ErrorBox error={pr.error} onRetry={pr.reload} />}

      {/* ---- Price changes ---- */}
      <SectionCard title={t("Menuprijs-wijzigingen")}
        subtitle={pr.data?.products_tracked ? `${pr.data.changed_count}/${pr.data.products_tracked} ${t("gevolgde producten wijzigden van prijs")} · ${pr.data.stable_count} ${t("stabiel")}` : ''}>
        <QueryState q={pr} isEmpty={(d) => !d.products_tracked} empty={t('Te korte periode voor prijstrends — kies minstens 4 maanden.')}>
          {(d) => (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("Prijsstijgingen")}</div>
                  {d.risers?.length ? <PriceTable rows={d.risers.slice(0, 10)} t={t} />
                    : <p className="text-sm text-slate-400 py-3">{t("Geen noemenswaardige stijgingen.")}</p>}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("Prijsdalingen")}</div>
                  {d.fallers?.length ? <PriceTable rows={d.fallers.slice(0, 10)} t={t} />
                    : <p className="text-sm text-slate-400 py-3">{t("Geen noemenswaardige dalingen.")}</p>}
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                {t("Op")} <b>{t("menuprijs")}</b> {t("(kaartprijs); zo tonen mix-effecten (gratis bolletjes, combo's) of kortingen zich niet als nep-prijswijziging.")}
              </p>
            </>
          )}
        </QueryState>
      </SectionCard>

      {/* ---- Employee discount ---- */}
      <SectionCard className="mt-4" title={t("Personeelskorting (≈50%)")}
        subtitle={t("De meest gebruikte korting is 50% — de personeelskorting. Pas sinds de nieuwe kassa (LightSpeed, mrt 2026) te volgen.")}>
        <QueryState q={ds} isEmpty={(d) => !d.employee} empty={t("Geen kortinggegevens in deze periode.")}>
          {(d) => (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <KpiTile label={t("Personeelsbonnen")} value={formatNumber(emp.orders)} sub={`${formatNumber(emp.lines)} ${t("bonregels")}`} accent="gold" icon={BadgePercent} />
                <KpiTile label={t("Toegekende korting")} value={formatCurrency(emp.discount_given)} sub={t("kost voor het café")} accent="gold" icon={Tag} />
                <KpiTile label={t("Effectief betaald")} value={formatCurrency(emp.amount_paid)} sub={`${t("bruto")} ${formatCurrency(emp.gross_value)}`} accent="green" icon={Tag} />
                <KpiTile label={t("Gem. korting")} value={`${n2(emp.avg_discount_pct)}%`} sub={t("komt overeen met de 50%-korting")} accent="mint" icon={BadgePercent} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("Per maand")}</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={empMonthly} margin={{ left: 0, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                      <Tooltip content={<ChartTooltip money={['discount']} />} />
                      <Bar dataKey="discount" name={t("Korting (€)")} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("Meest met personeelskorting")}</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[280px]">
                      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                        <th className="py-1">{t("Product")}</th><th className="text-right">{t("stuks")}</th><th className="text-right">{t("korting")}</th>
                      </tr></thead>
                      <tbody>
                        {(d.top_items || []).slice(0, 8).map((it) => (
                          <tr key={it.name} className="border-t border-slate-100">
                            <td className="py-1.5 text-slate-700"><span className="block truncate max-w-[180px]" title={it.name}>{it.name}</span></td>
                            <td className="text-right tabular-nums">{formatNumber(it.items)}</td>
                            <td className="text-right tabular-nums">{formatCurrency2(it.discount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                {t("Daarnaast: losse promokortingen")} ({formatNumber(d.promo?.lines)} {t("regels")}, {formatCurrency(d.promo?.discount)})
                {' '}{t("en grote bon/voucher-correcties")} ({formatNumber(d.voucher?.lines)} {t("regels")}) — {t("apart gehouden zodat de cijfers zuiver blijven. In de oude kassa werd korting nauwelijks geregistreerd.")}
              </p>
            </>
          )}
        </QueryState>
      </SectionCard>
    </PageShell>
  );
}
